import React, { useCallback, useState } from "react";
import { Formik, Form } from "formik";
import Button from "@adsk/alloy-react-button";
import Modal from "@adsk/alloy-react-modal";
import TextInput from "@adsk/alloy-react-text-input";
import Textarea from "@adsk/alloy-react-textarea";
import { EscapePressHandler } from "../../../../utils/components/EscapePressHandler";
import { DiscardModal } from "../../../../utils/components/DiscardModal";
import {
  FieldMetadataForTypes as FieldMetadata,
  IssueStatus,
  IssueType,
  PermittedAction,
  IssueStandardFieldIds,
} from "@adsk/acc-issues-types";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { getIssueTypeEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueTypes";
import { useHydratedRef } from "@adsk/acc-issues-common/hooks/dom/useHydratedRef";
import { useTranslations } from "@adsk/acc-issues-translations";
import { FieldWrapper } from "@adsk/acc-issues-common/components/primitive/FieldWrapper";
import { IssueFormAttributes } from "@adsk/acc-issues-core/utils/types/Issue";
import { WatcherDropdown } from "@adsk/acc-issues-common/components/input/WatcherDropdown";
import { WatcherFieldLabel } from "@adsk/acc-issues-common/components/display/WatcherFieldLabel";
import { AssigneeDropdown } from "@adsk/acc-issues-common/components/input/ProjectMemberDropdown/AssigneeDropdown";
import { useGetLevelOneUserShouldIncludeMemberFn } from "@adsk/acc-issues-common/hooks/data/useGetLevelOneUserShouldIncludeMemberFn";
import { getCurrentUserPermittedActionsForNewIssue } from "@adsk/acc-issues-core/state/redux/ducks/currentUserPermissions";
import { LocationCreateField } from "../../../../issue-creation-components/LocationCreateField";
import { DateField } from "@adsk/acc-issues-common/components/input/DateField";
import { useCustomFieldIdsWithMappingsForType } from "../../../../utils/hooks/issueCustomFieldMappingHooks";
import { RootCauseDropdown } from "@adsk/acc-issues-common/components/input/RootCauseDropdown";
import { CustomFieldWithFormikContext } from "../../../../input-field-components/CustomFieldWithFormikContext";
import { useIssueTypeChangeValidationSchema } from "./useIssueTypeChangeValidationSchema";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { useWatcherPropName } from "../../../../utils/hooks/watchers/useWatcherPropName";
import { WatcherObject } from "@adsk/acc-issues-types";
import { useGetWatchersValue } from "../../../../utils/hooks/watchers/useGetWatchersValue";

interface IssueTypeChangeModalProps {
  pendingTypeId: string;
  // these are fields required for the pending type
  requiredFieldsMetadata: FieldMetadata[];
  onComplete: (attributes: Partial<IssueFormAttributes>) => void;
  onClose: () => void;
}

const IssueTypeChangeModalContent = ({
  pendingTypeId,
  requiredFieldsMetadata,
  onComplete,
  onClose,
}: IssueTypeChangeModalProps) => {
  const ref = useHydratedRef();
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const issueTypeEntitiesMap = useSelector(getIssueTypeEntitiesMap);
  const pendingType: IssueType | undefined =
    issueTypeEntitiesMap[pendingTypeId];

  const issueTypeTitle = pendingType?.title || "";

  const validationSchema = useIssueTypeChangeValidationSchema({
    requiredFieldsMetadata,
  });

  const initialValues: Partial<IssueFormAttributes> = {
    issueTypeId: pendingTypeId,
    // we do not submit the status: it is just used for the validation schema,
    // so we set it to "Open" because it will correctly require the fields
    status: IssueStatus.Open,
    customAttributes: {},
  };

  const { value: shouldHideEmptyRolesAndCompanies } = useFeatureFlag(
    FeatureFlag.HideEmptyRolesAndCompanies
  );

  const handleFormSubmission = useCallback(
    (attributes: Partial<IssueFormAttributes>) => {
      const attributeChanges = {
        ...attributes,
      };
      delete attributeChanges.status;
      onComplete(attributeChanges);
    },
    [onComplete]
  );

  const permittedActions = useSelector(
    getCurrentUserPermittedActionsForNewIssue
  );

  const levelOneUserShouldIncludeMemberFn =
    useGetLevelOneUserShouldIncludeMemberFn();
  const shouldIncludeMemberFn = !permittedActions.includes(
    PermittedAction.AssignAll
  )
    ? levelOneUserShouldIncludeMemberFn
    : undefined;

  const { mappingsByCustomFieldId } =
    useCustomFieldIdsWithMappingsForType(pendingTypeId);

  const { T } = useTranslations();

  const watchersPropName = useWatcherPropName();
  const { getWatchersValue } = useGetWatchersValue();
  const getSelectedWatcherIds = (values: Partial<IssueFormAttributes>) => {
    return watchersPropName === "watchers"
      ? values[watchersPropName]
      : values[watchersPropName]?.map(x => x.id);
  };

  if (!pendingType) {
    // this should in practice never happen, but the component on its own cannot know that
    console.error(
      `Type with ID ${pendingTypeId} not found. Cannot show type change form.`
    );
    return (
      <div style={{ padding: 32 }} onClick={onClose}>
        {T.translate("tryAgain")}
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmission}
      validationSchema={validationSchema}
    >
      {({
        handleSubmit,
        values,
        setValues,
        handleChange,
        setFieldValue,
        touched,
        errors,
        submitCount,
      }) => {
        return (
          <div>
            <Modal.Header>
              {T.translate("issueEditForm.changeIssueType.completeNewFields")}{" "}
              <Modal.Close onClick={() => setIsDiscardModalOpen(true)} />
            </Modal.Header>
            <EscapePressHandler
              onEscapePress={() => setIsDiscardModalOpen(true)}
            />
            <Modal.Body style={{ maxHeight: "70vh", padding: "16px 24px" }}>
              <div>
                {T.translate(
                  "issueEditForm.changeIssueType.additionalFieldsRequired",
                  { issueTypeTitle }
                )}
              </div>
              <div>
                {T.translate("issueEditForm.changeIssueType.fillToProceed")}
              </div>
              <Form ref={ref}>
                {requiredFieldsMetadata.map((field: FieldMetadata) => {
                  const { id: fieldId } = field;
                  if (fieldId === IssueStandardFieldIds.Description) {
                    return (
                      <FieldWrapper
                        key={fieldId}
                        label={T.translate("issueAttributes.description")}
                        errorMessage={
                          touched.description || submitCount > 0
                            ? errors.description
                            : undefined
                        }
                        required={true}
                      >
                        <Textarea
                          style={{ width: "100%" }}
                          // @ts-ignore https://jira.autodesk.com/browse/DSGN-661
                          name="description"
                          autoComplete="off"
                          value={values.description}
                          onChange={handleChange}
                          maxHeight={70}
                          minHeight={70}
                          placeholder={
                            T.translate(
                              "issueCreation.description.placeholder"
                            ) as string
                          }
                          error={
                            (touched.description || submitCount > 0) &&
                            !!errors.description
                          }
                          data-testid="issue-type-change-modal-description"
                        />
                      </FieldWrapper>
                    );
                  } else if (fieldId === IssueStandardFieldIds.Assignee) {
                    return (
                      <FieldWrapper
                        key={fieldId}
                        label={T.translate("issueAttributes.assignedTo")}
                        errorMessage={
                          touched.assignedTo || submitCount > 0
                            ? errors.assignedTo
                            : undefined
                        }
                        required={true}
                      >
                        <AssigneeDropdown
                          shouldIncludeMemberFn={shouldIncludeMemberFn}
                          selectedAssigneeId={values.assignedTo}
                          selectedWatchers={values.watcherObjects}
                          onChange={({ assignedTo, assignedToType }) => {
                            const newValues: Partial<IssueFormAttributes> = {
                              ...values,
                              assignedTo: assignedTo,
                              assignedToType: assignedToType,
                            };
                            setValues(newValues);
                          }}
                          clearable={permittedActions.includes(
                            PermittedAction.ClearAssignee
                          )}
                          shouldIncludeEmpty={!shouldHideEmptyRolesAndCompanies}
                        />
                      </FieldWrapper>
                    );
                  } else if (fieldId === IssueStandardFieldIds.Watchers) {
                    return (
                      <FieldWrapper
                        key={fieldId}
                        label={<WatcherFieldLabel />}
                        errorMessage={
                          touched[watchersPropName] || submitCount > 0
                            ? errors[watchersPropName]
                            : undefined
                        }
                        required={true}
                      >
                        <WatcherDropdown
                          shouldIncludeMemberFn={shouldIncludeMemberFn}
                          selectedWatcherIds={getSelectedWatcherIds(values)}
                          onChange={(
                            newSelectedWatcherObjects: WatcherObject[]
                          ) => {
                            setFieldValue(
                              watchersPropName,
                              getWatchersValue(newSelectedWatcherObjects)
                            );
                          }}
                          data-testid="issue-type-change-modal-watchers"
                          invalid={
                            (touched[watchersPropName] || submitCount > 0) &&
                            !!errors[watchersPropName]
                          }
                          shouldIncludeEmpty={false}
                        />
                      </FieldWrapper>
                    );
                  } else if (fieldId === IssueStandardFieldIds.Location) {
                    return (
                      <LocationCreateField
                        key={fieldId}
                        isRequired={true}
                        errorMessage={
                          touched.locationId || submitCount > 0
                            ? errors.locationId
                            : undefined
                        }
                      />
                    );
                  } else if (
                    fieldId === IssueStandardFieldIds.LocationDetails
                  ) {
                    return (
                      <FieldWrapper
                        key={fieldId}
                        label={T.translate("issueAttributes.locationDetails")}
                        errorMessage={
                          touched.locationDetails || submitCount > 0
                            ? errors.locationDetails
                            : undefined
                        }
                        required={true}
                      >
                        <TextInput
                          name="locationDetails"
                          autoComplete="off"
                          value={values.locationDetails}
                          onChange={handleChange}
                          placeholder={
                            T.translate(
                              "issueCreation.locationDetails.placeholder"
                            ) as string
                          }
                          error={
                            (touched.locationDetails || submitCount > 0) &&
                            !!errors.locationDetails
                          }
                          data-testid="issue-type-change-modal-locationdetails"
                        />
                      </FieldWrapper>
                    );
                  } else if (fieldId === IssueStandardFieldIds.DueDate) {
                    return (
                      <FieldWrapper
                        key={fieldId}
                        label={T.translate("issueAttributes.dueDate")}
                        errorMessage={
                          touched.dueDate || submitCount > 0
                            ? errors.dueDate
                            : undefined
                        }
                        required={true}
                      >
                        <DateField
                          date={values.dueDate}
                          onChange={newDate =>
                            setFieldValue("dueDate", newDate)
                          }
                          data-testid="issue-type-change-modal-duedate"
                          error={
                            (touched.dueDate || submitCount > 0) &&
                            !!errors.dueDate
                          }
                        />
                      </FieldWrapper>
                    );
                  } else if (fieldId === IssueStandardFieldIds.StartDate) {
                    return (
                      <FieldWrapper
                        key={fieldId}
                        label={T.translate("issueAttributes.startDate")}
                        errorMessage={
                          touched.startDate || submitCount > 0
                            ? errors.startDate
                            : undefined
                        }
                        required={true}
                      >
                        <DateField
                          date={values.startDate}
                          onChange={newDate =>
                            setFieldValue("startDate", newDate)
                          }
                          data-testid="issue-type-change-modal-startdate"
                          error={
                            (touched.startDate || submitCount > 0) &&
                            !!errors.startDate
                          }
                        />
                      </FieldWrapper>
                    );
                  } else if (
                    fieldId === IssueStandardFieldIds.RootCauseWithCategory
                  ) {
                    return (
                      <FieldWrapper
                        key={fieldId}
                        label={T.translate("issueAttributes.rootCause")}
                        errorMessage={
                          touched.rootCauseId || submitCount > 0
                            ? errors.rootCauseId
                            : undefined
                        }
                        required={true}
                      >
                        <RootCauseDropdown
                          selectedRootCauseId={values.rootCauseId}
                          onChange={({ rootCauseId }) =>
                            setFieldValue("rootCauseId", rootCauseId)
                          }
                          data-testid="issue-type-change-modal-rootcause"
                          invalid={
                            (touched.rootCauseId || submitCount > 0) &&
                            !!errors.rootCauseId
                          }
                        />
                      </FieldWrapper>
                    );
                  } else if (
                    field.type === "custom" &&
                    mappingsByCustomFieldId[field.id]
                  ) {
                    const mapping = mappingsByCustomFieldId[field.id];
                    return (
                      <CustomFieldWithFormikContext
                        {...mapping}
                        key={fieldId}
                        isRequired={true}
                      />
                    );
                  }
                })}
              </Form>
            </Modal.Body>
            <DiscardModal
              isOpen={isDiscardModalOpen}
              onDiscard={onClose}
              closeDiscardModal={() => setIsDiscardModalOpen(false)}
            />
            <Modal.Footer>
              <Button
                variant={Button.VARIANTS.SECONDARY}
                onClick={() => setIsDiscardModalOpen(true)}
              >
                {T.translate("actions.cancel")}
              </Button>
              <Button
                style={{
                  marginLeft: 16,
                }}
                variant={Button.VARIANTS.PRIMARY}
                onClick={() => handleSubmit()}
              >
                {T.translate("actions.saveChanges")}
              </Button>
            </Modal.Footer>
          </div>
        );
      }}
    </Formik>
  );
};

export const IssueTypeChangeModal = ({
  ...props
}: IssueTypeChangeModalProps) => {
  // for performance reasons we only render this component when there is a pending type id;
  // not having the modal fade in is an acceptable tradeoff
  if (!props.pendingTypeId) {
    return null;
  }
  return (
    <Modal open size={Modal.SIZES.MEDIUM} height="inherit">
      <IssueTypeChangeModalContent key={props.pendingTypeId} {...props} />
    </Modal>
  );
};
